import { api } from '@/lib/api';
import type { Certificate } from '@/features/certificates/types';
import type { WorkOrder } from '@/features/work-orders/types';
import type {
  CancelDrydockCampaignInput,
  CreateDrydockCampaignInput,
  UpdateDrydockCampaignInput,
} from './schema';
import type { DrydockCampaign, DrydockCampaignStatus } from './types';

/**
 * List filters accepted by the parent-scoped endpoints. Backend
 * exposes a single ?status query parameter today; we keep the shape
 * extensible so future filters (e.g. ?yearStart) plug in without a
 * service signature change.
 */
export interface DrydockCampaignListFilters {
  status?: DrydockCampaignStatus;
}

/**
 * Drydock campaigns REST client (Phase 5b).
 *
 * Routes are scattered across /companies/:id/..., /vessels/:id/...,
 * and /drydock-campaigns/:id/... so the service exposes one method
 * per route shape rather than a generic list.
 *
 * Endpoints:
 *   GET    /drydock-campaigns/:id                              detail
 *   GET    /companies/:companyId/drydock-campaigns             list by company
 *   GET    /vessels/:vesselId/drydock-campaigns                list by vessel
 *   GET    /drydock-campaigns/:id/work-orders                  linked work orders
 *   GET    /drydock-campaigns/:id/certificates                 renewed certificates
 *   POST   /drydock-campaigns                                  create
 *   PATCH  /drydock-campaigns/:id                              update editable fields
 *   POST   /drydock-campaigns/:id/start                        planned → in_progress
 *   POST   /drydock-campaigns/:id/complete                     in_progress → completed
 *   POST   /drydock-campaigns/:id/cancel                       any non-cancelled → cancelled
 *   DELETE /drydock-campaigns/:id                              soft-delete (admin)
 */
export const drydockCampaignsApi = {
  getById: async (id: string): Promise<DrydockCampaign> => {
    const { data } = await api.get<DrydockCampaign>(`/drydock-campaigns/${id}`);
    return data;
  },

  listByCompany: async (
    companyId: string,
    filters: DrydockCampaignListFilters = {},
  ): Promise<DrydockCampaign[]> => {
    const { data } = await api.get<DrydockCampaign[]>(
      `/companies/${companyId}/drydock-campaigns`,
      { params: filters },
    );
    return data;
  },

  listByVessel: async (
    vesselId: string,
    filters: DrydockCampaignListFilters = {},
  ): Promise<DrydockCampaign[]> => {
    const { data } = await api.get<DrydockCampaign[]>(
      `/vessels/${vesselId}/drydock-campaigns`,
      { params: filters },
    );
    return data;
  },

  /**
   * Work orders linked through `WorkOrder.drydockCampaignId`. The
   * backend returns the bare WO list, not paginated, since a single
   * campaign rarely exceeds a few dozen orders.
   */
  listWorkOrders: async (id: string): Promise<WorkOrder[]> => {
    const { data } = await api.get<WorkOrder[]>(
      `/drydock-campaigns/${id}/work-orders`,
    );
    return data;
  },

  /**
   * Certificates renewed during the campaign — those whose
   * `renewedDuringDrydockCampaignId` points at this campaign id.
   */
  listCertificates: async (id: string): Promise<Certificate[]> => {
    const { data } = await api.get<Certificate[]>(
      `/drydock-campaigns/${id}/certificates`,
    );
    return data;
  },

  create: async (
    input: CreateDrydockCampaignInput,
  ): Promise<DrydockCampaign> => {
    const { data } = await api.post<DrydockCampaign>(
      '/drydock-campaigns',
      input,
    );
    return data;
  },

  update: async (
    id: string,
    input: UpdateDrydockCampaignInput,
  ): Promise<DrydockCampaign> => {
    const { data } = await api.patch<DrydockCampaign>(
      `/drydock-campaigns/${id}`,
      input,
    );
    return data;
  },

  start: async (id: string): Promise<DrydockCampaign> => {
    const { data } = await api.post<DrydockCampaign>(
      `/drydock-campaigns/${id}/start`,
    );
    return data;
  },

  complete: async (id: string): Promise<DrydockCampaign> => {
    const { data } = await api.post<DrydockCampaign>(
      `/drydock-campaigns/${id}/complete`,
    );
    return data;
  },

  cancel: async (
    id: string,
    input: CancelDrydockCampaignInput,
  ): Promise<DrydockCampaign> => {
    const { data } = await api.post<DrydockCampaign>(
      `/drydock-campaigns/${id}/cancel`,
      input,
    );
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/drydock-campaigns/${id}`);
  },
};
